import type { CardProps } from "@heroui/react";
import { Button, Card, CardBody, CardFooter, CardHeader, Input as NextInput } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import React from "react";
import { Input } from "~/components";
import { LanguageSelector } from "~/components/input/selector/language-selector";
import { useAuth } from "~/modules/auth/use-auth";
import { useSetLocale } from "~/utils/i18n/i18n-hooks";
import { useAuthAction } from "../../data";

export default function Profile(props: CardProps) {
  const { user } = useAuth();
  const { updateUserProfile } = useAuthAction();

  const [hasChanged, setHasChanged] = React.useState(false);

  const [phone, setPhone] = React.useState(user?.phone ?? "");
  const [firstName, setFirstName] = React.useState(user?.firstName ?? "");
  const [lastName, setLastName] = React.useState(user?.lastName ?? "");
  const [dateOfBirth, setDateOfBirth] = React.useState(
    user?.dateOfBirth ? new Date(user?.dateOfBirth).toISOString().split("T")[0] : "",
  );
  const [preferredLanguage, setPreferredLanguage] = React.useState(user?.preferredLanguage ?? "en");
  const setLocale = useSetLocale();

  return (
    <Card
      className="max-w-xl p-2"
      {...props}
    >
      <CardHeader className="flex flex-col items-start px-4 pb-0 pt-4">
        <p className="text-large">
          <Trans>Account Details</Trans>
        </p>
      </CardHeader>
      <CardBody className="w-full grid grid-cols-1 gap-4">
        <Input.Phone
          name="phone"
          label={t`Phone Number`}
          defaultValue={phone}
          onChange={(v) => {
            setPhone(v);
            setHasChanged(true);
          }}
        />
        {/* First Name */}
        <NextInput
          label={t`First Name`}
          labelPlacement="outside"
          placeholder={t`Enter first name`}
          defaultValue={firstName}
          onValueChange={(v) => {
            setFirstName(v);
            setHasChanged(true);
          }}
        />
        {/* Last Name */}
        <NextInput
          label={t`Last Name`}
          labelPlacement="outside"
          placeholder={t`Enter last name`}
          defaultValue={lastName}
          onValueChange={(v) => {
            setLastName(v);
            setHasChanged(true);
          }}
        />
        {/* Date of Birth */}
        <NextInput
          type="date"
          name="dateOfBirth"
          max={"2099-12-31"}
          labelPlacement="outside"
          placeholder={t`Enter your date of birth`}
          onValueChange={(v) => {
            setDateOfBirth(v);
            setHasChanged(true);
          }}
          defaultValue={dateOfBirth}
          label={t`Date of Birth`}
        />

        {/* Preferred Language */}
        <LanguageSelector
          name="preferredLanguage"
          defaultValue={preferredLanguage}
          onChange={(v) => {
            setPreferredLanguage(v);
            setHasChanged(true);
          }}
        />
      </CardBody>

      <CardFooter className="mt-4 justify-end gap-2">
        <Button
          isDisabled={!hasChanged}
          color="primary"
          radius="full"
          onPress={() => {
            setLocale(preferredLanguage);
            updateUserProfile({
              intent: "user:profile",
              phone,
              firstName,
              lastName,
              dateOfBirth,
              preferredLanguage,
            });
            setHasChanged(false);
          }}
        >
          <Trans>Save Changes</Trans>
        </Button>
      </CardFooter>
    </Card>
  );
}
